import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import pkceChallenge from 'pkce-challenge';

import { selectIsAuthenticated, selectProduct, userSelector } from 'stash/selectors';

import useNudata from 'stash/hooks/nudata';
import { generateRandomString } from 'stash/utils/random';
import { buildAuthRedirect } from 'stash/utils/helpers';
import { deleteCookie, getCookie, setCookie } from 'stash/utils/cookies';
import { getURLParam } from 'stash/utils';
import { PROMO_PARAM_NAME, PROMO_V2_PARAM_NAME } from 'stash/middleware/promo';
import {
	CODE_VERIFIER_COOKIE_NAME,
	PRODUCT_COOKIE_NAME,
	STATE_PARAM_COOKIE_NAME,
} from 'stash/constants/cookies';
import { EXPERIMENTS, useIsInExperimentVariation } from 'stash/components/experiments';
import { OnboardingStepsVoaM1 } from '../OnboardingGraphVoAM1';
import { UniversalOnboardingSteps } from '../UniversalOnboardingGraph';
import { BASE_SIGNUP_URLS, BASE_SIGNUP_URLS_V2 } from 'stash/constants/authParams';

const useAuthRedirect = () => {
	const newAuthFlowEnabled = useIsInExperimentVariation(
		EXPERIMENTS.IS_WEB_AUTH_V2_ENABLED,
		true
	);
	const history = useHistory();
	const [authRedirect, setAuthRedirect] = useState(null);
	const product = useSelector(selectProduct);
	const user = useSelector(userSelector);
	const isAuthenticated = useSelector(selectIsAuthenticated);
	const { nudataHasError, nudataQueryParams } = useNudata();
	const isInVoiceOfAdvisorVariation = useIsInExperimentVariation(
		EXPERIMENTS.VOICE_OF_THE_ADVISOR_V1,
		true
	);

	useEffect(() => {
		if (isAuthenticated) {
			if (user?.created_at) {
				const cachedProduct = getCookie(PRODUCT_COOKIE_NAME);
				deleteCookie(PRODUCT_COOKIE_NAME);
				const defaultPath = isInVoiceOfAdvisorVariation
					? OnboardingStepsVoaM1[Object.keys(OnboardingStepsVoaM1)[0]].path
					: UniversalOnboardingSteps[Object.keys(UniversalOnboardingSteps)[0]].path;
				const productString = !product && cachedProduct ? '-' + cachedProduct : '';
				const targetPath = `/${
					window.location.pathname.split('/')[1]
				}${productString}${defaultPath}`;
				if (history.location.pathname !== targetPath) {
					const redirectTo = `${targetPath}${window.location.search}`;
					return history.push(redirectTo);
				}
			}
		} else {
			// ensure nudata session has been initialized before redirecting
			if (nudataQueryParams || nudataHasError) {
				//generate necessary authv2 security params
				const stateParam = generateRandomString();
				const { code_verifier, code_challenge } = pkceChallenge();

				//clear out any existing authv2 state param and set a new one in a cookie
				deleteCookie(STATE_PARAM_COOKIE_NAME);
				setCookie(STATE_PARAM_COOKIE_NAME, stateParam);

				//clear out any existing authv2 code verifier and set a new one in a cookie
				deleteCookie(CODE_VERIFIER_COOKIE_NAME);
				setCookie(CODE_VERIFIER_COOKIE_NAME, code_verifier);

				//save product in a cookie so that we can return to it after authV2 redirect
				if (product) {
					deleteCookie(PRODUCT_COOKIE_NAME);
					setCookie(PRODUCT_COOKIE_NAME, product);
				}

				const promoCode = getURLParam(PROMO_PARAM_NAME);
				const promoCodeV2 = getURLParam(PROMO_V2_PARAM_NAME);

				const baseUrl = newAuthFlowEnabled
					? BASE_SIGNUP_URLS_V2[window.Stash.env]
					: BASE_SIGNUP_URLS[window.Stash.env];

				const authRedirect = buildAuthRedirect({
					env: window.Stash.env,
					stateParam,
					codeChallenge: code_challenge,
					nudataQueryParams,
					stash_code: promoCode,
					stash_code_v2: promoCodeV2,
					product,
					baseUrl,
					version: window.Stash.version,
				});

				//redirect to Hodor for username creation
				setAuthRedirect(authRedirect);
			}
		}
	}, [
		isAuthenticated,
		nudataHasError,
		nudataQueryParams,
		product,
		history,
		user.created_at,
		isInVoiceOfAdvisorVariation,
		newAuthFlowEnabled,
	]);

	return {
		authRedirect,
	};
};

export default useAuthRedirect;
